import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import { formatDateForDB } from "@asap/shared/utils/date";
import { normalizeString } from "@asap/shared/utils/string";
import type { SupabaseFile } from "@asap/shared/utils/supabase.types";
import { downloadZip } from "client-zip";
import { saveAs } from "file-saver";

export type SupabaseFileDownloadable = { downloadUrl: string } & SupabaseFile;

export const useFile = () => {
  const supabase = useSupabase();
  const { invoke } = useApi();

  const selectSingle = async function (id: string) {
    if (!id) return;

    const response = await invoke({ path: "file/:id", method: "GET", params: { id } });
    if (!response.success) throw new Error("Failed to select file");
    return response.data;
  };

  const removeSingle = async function (id: string) {
    const response = await invoke({ path: "file/:id", method: "DELETE", params: { id } });
    if (!response.success) throw new Error("Failed to delete file");
  };

  const getSupabaseStorageUrl = async (file: Partial<SupabaseFile>) => {
    if (!file.supabase_bucket_id) throw new Error("File has no supabase_bucket_id");
    if (!file.supabase_name) throw new Error("File has no supabase_name");

    const expiresIn = 600; // 10 minutes in seconds, we want to generate a signed URL that is valid for 10 minutes

    const { data, error } = await supabase.storage
      .from(file.supabase_bucket_id)
      .createSignedUrl(file.supabase_name, expiresIn);
    if (error) throw new Error(error.message);

    return data.signedUrl ?? "";
  };

  const addDownloadUrl = (files: SupabaseFile[]): Promise<SupabaseFileDownloadable[]> => {
    return Promise.all(
      files.map(async (file) => {
        return { ...file, downloadUrl: await getSupabaseStorageUrl(file) };
      })
    );
  };

  const downloadFilesAsZip = async (files: SupabaseFileDownloadable[], title?: string) => {
    // We need to normalize the title to remove accents and special characters
    // We need format using date and replacing space and special characters left by "_"
    const formattedTitle = normalizeString(title ?? files[0].name).replace(/[^a-zA-Z0-9]/g, "_");
    const finalTitle = `${formatDateForDB(new Date())}-${formattedTitle}`;
    const fetchedFiles = await Promise.all(
      files.map(async (file, index) => {
        const result = await fetch(file.downloadUrl);
        const input = await result.blob();
        const fileName = title ? `${title}${index > 0 ? "-" + index : ""}` : file.name;
        const extension = file.mimetype.split("/")[1];
        const name = `${fileName}.${extension}`;
        return { input, name };
      })
    );
    const blob = await downloadZip(fetchedFiles).blob();
    saveAs(blob, finalTitle);
  };

  const downloadFiles = async (files: SupabaseFile[], title?: string) => {
    const filesWithDownloadableUrl = await addDownloadUrl(files);

    if (filesWithDownloadableUrl.length === 1) {
      await downloadFile(filesWithDownloadableUrl[0], title);
      return;
    }

    await downloadFilesAsZip(filesWithDownloadableUrl, title);
  };

  const downloadFile = async (
    file: Pick<SupabaseFileDownloadable, "downloadUrl" | "mimetype" | "name">,
    title?: string
  ) => {
    const data = await fetch(file.downloadUrl);
    const blobPart = await data.blob();
    const blob = new Blob([blobPart], { type: file.mimetype });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = title ?? file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return {
    downloadFile,
    downloadFiles,
    downloadFilesAsZip,
    selectSingle,
    removeSingle,
    getSupabaseStorageUrl,
    addDownloadUrl,
  };
};
